
import {
    Component,
    Input,
    OnInit,
    effect,
    inject,
    input,
    model,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import {
    AppPermissions,
    AuthenticationService,
    IRole,
    IUserAccountDto,
} from '@my-aon/auth';
import {
    SharedModule,
    StoreService
} from '@my-aon/shared';
import {
    AdminAccountsService,
    PermissionViewModel,
    RolesService,
} from '../../../lib';
import { RolePermissionsListComponent } from '../role-permissions-list/role-permissions-list.component';
import { AccountClientsListComponent } from '../account-clients-list/account-clients-list.component';
import { hasValue, mapFormAddTranslatedLabels, validateGroup, DxFormItem } from '@my-aon/utils';

@Component({
    selector: 'lib-form-account-details',
    templateUrl: './form-account-details.component.html',
    styleUrl: './form-account-details.component.scss',
    imports: [
        SharedModule,
        RolePermissionsListComponent,
        AccountClientsListComponent
    ],
})
export class FormAccountDetailsComponent implements OnInit {

    readonly translateService: TranslateService = inject(TranslateService);
    readonly storeService: StoreService = inject(StoreService);
    readonly authService: AuthenticationService = inject(AuthenticationService);
    readonly accountsService: AdminAccountsService = inject(AdminAccountsService);
    readonly rolesService: RolesService = inject(RolesService);

    $submited = model<boolean>(false, { alias: 'submited' });
    onClose = output<IUserAccountDto | undefined>();

    $data = model.required<IUserAccountDto>({ alias: 'data' });
    formData: IUserAccountDto = { Enabled: true, HWContext: 'EB' } as IUserAccountDto;

    originalData?: IUserAccountDto;

    $readOnly = model.required<boolean>({ alias: 'readOnly' });

    $validationGroupName = input<string>('FormAccountDetailsComponent' + 'ValidationGroupName', { alias: 'validationGroupName' });
    validate = async () => await validateGroup(this.$validationGroupName());

    $grid = viewChild<AccountClientsListComponent>(AccountClientsListComponent);

    tab: 'form' | 'permissions' = 'form';

    effectivePermissions: PermissionViewModel[] = [];
    selectableRoles: Array<IRole & { disabled?: boolean }> = [];

    get userRoles() {
        return this.formData?.RoleList ?? [];
    }

    get isNew() {
        return !hasValue(this.formData?.Id);
    }

    loadIndicatorVisible: boolean = true;

    // Form items
    items: DxFormItem[] = [];
    selectedGroup: any;

    constructor() {
        effect(() => {
            const data = this.$data();

            if (data && hasValue(data)) {
                if (hasValue(data?.Id)) this.reload();
                else {
                    setTimeout(() => {
                        this.regenerateModel(data);
                    }, 23);
                }
            }
        });

        effect(() => {
            this.$submited() && this.formSubmit();
        });
    }

    ngOnInit(): void {
        this.validate();
    }

    reload() {
        this.loadIndicatorVisible = true;
        this.accountsService.byKey(this.$data()?.Id)
            .subscribe({
                next: (model: IUserAccountDto) => {
                    this.regenerateModel(model);
                },
            });
    }

    async regenerateModel(model: IUserAccountDto) {
        this.formData = model;
        this.originalData = { ...model };

        if (this.isNew) {
            this.formData.Enabled = true;
        }

        await this.regenerateSelectableRoles();

        let readOnly = !this.authService.hasAnyPermission(AppPermissions.SYS_AUTH_USERS_MANAGE_USERS);

        if (!this.isNew) {

            if (this.originalData?.IsExternal && !this.authService.hasAnyPermission(AppPermissions.SYS_AUTH_USERS_MANAGE_USERS_EXTERNAL))
                readOnly = true;

            if (!this.originalData?.IsExternal && !this.authService.hasAnyPermission(AppPermissions.SYS_AUTH_USERS_MANAGE_USERS_INTERNAL))
                readOnly = true;

            /* @TODO
            if (minPriority != 0 && this.originalData.MinimumUserRolePriority <= minPriority)
                readOnly = true; */
        }

        if (readOnly != this.$readOnly()) this.$readOnly.set(readOnly);

        this.effectivePermissions = this.rolesService.$permissions().map(x => {

            return (new PermissionViewModel({ ...x, active: this.formData?.PermissionList?.some(y => y == x.key) ?? false }));
        })

        await this.generateFormItems();
        this.validate();

        this.loadIndicatorVisible = false;
    }

    async regenerateSelectableRoles() {

        const roles = this.rolesService.$roles();

        let isExternal: boolean = hasValue(this.originalData?.IsExternal) ? this.originalData?.IsExternal : hasValue(this.formData?.IsExternal) ? this.formData?.IsExternal : true

        this.rolesService.getEligibleRolesForUser(this.originalData?.Id, isExternal)
            .pipe(map(eligibleRoles => {

                this.selectableRoles = roles.map((x) => {
                    (x as any).disabled = !eligibleRoles.some((y) => y.Id == x.Id) || (this.formData?.IsExternal == true && x.SystemRole == true);
                    return x;
                });

                return true;
            }))
            //regenerate form after updated roles list
            .subscribe(_ => this.generateFormItems());

    }

    formSubmitTrigger: boolean = false;
    formSubmit = async (e?: any) => {
        if (this.loadIndicatorVisible)
            //already submited
            return;

        if (!(await this.validate()) || this.tab != 'form') {
            this.$submited.set(false);
            return;
        }

        this.submit();
    };

    private submit = async () => {
        this.loadIndicatorVisible = true;

        const cancel = () => {
            this.$submited.set(false);
            this.loadIndicatorVisible = false;
        };

        const created = (newPcp: IUserAccountDto) => {
            this.close(newPcp);
        };

        if (!hasValue(this.formData?.Id)) {
            delete (this.formData as any)?.Id; // Prevent Id = Null from breaking API until refactor is done to send regular json instead of FormData.
        }

        return this.accountsService
            .insertOrUpdate(this.formData!, this.originalData!)
            .subscribe({
                next: (data) => {
                    created(data);
                },
                error: (error) => {
                    console.error(error);
                    cancel();
                },
            });
    };

    close = (pcp: IUserAccountDto) => {
        this.onClose.emit(pcp ? pcp : this.formData);
    };

    validationCallbackEmail = async (options: {value: any}) => {

        if (!this.isNew) return true;

        this.loadIndicatorVisible = true;

        return await firstValueFrom(
            this.accountsService
                .getOktaUserByEmail(options.value)
                .pipe(
                    map((result) => {
                        if (result) {
                            this.formData = result;
                            this.regenerateSelectableRoles();

                        }
                        return true;
                    })
                )
        );
    };

    async generateFormItems() {

        this.loadIndicatorVisible = true;

        let dataSourceClientGroups = this.storeService.DataSourceOptions('Admin/ClientGroups', 'Id',
            {
                sort: [{ selector: 'Name' },],
                select: ['Id', 'Name'],
            },
        );

        let formClientGroupVisible = (() => {
            if (!this.formData?.RoleList || this.formData.RoleList.length === 0) return false;
            if (this.formData?.RoleList?.length > 1) return true;

            return this.formData?.RoleList?.some((role: string) => role !== 'SysManager');
        })();

        let dataFieldClientGroupRequired = (() => {


            if (!this.formData?.RoleList || this.formData.RoleList.length === 0) return false;
            //if (this.formData?.RoleList?.length > 1) return true;

            return this.formData?.RoleList?.some((role: string) => (role !== 'SysManager' && role !== 'SysAdmin'));
        })();

        this.items = (
            [
                //
                // USER INFORMATION
                //
                {
                    itemType: 'group',
                    colCount: 2,
                    caption: 'User',
                    items: [

                        {
                            dataField: 'Email',
                            isRequired: true,
                            editorOptions: {
                                mode: 'email',
                                readOnly: !this.isNew,
                            },
                            colSpan: 1,
                            validationRules: [
                                { type: 'required' },
                                { type: 'email' },
                                {
                                    type: 'async',
                                    validationCallback: this.validationCallbackEmail.bind(this)
                                }
                            ],
                        },

                        {
                            dataField: 'Name',
                            isRequired: true,
                            colSpan: 1,
                            editorOptions: {
                                readOnly: !this.isNew,
                            },
                        },

                        {
                            dataField: 'RoleList',
                            isRequired: true,
                            editorType: 'dxTagBox',
                            editorOptions: {
                                searchEnabled: true,
                                showClearButton: true,
                                openOnFieldClick: true,
                                showSelectionControls: true,
                                showDropDownButton: true,
                                dataSource: this.selectableRoles,
                                valueExpr: 'Name',
                                displayExpr: 'Name',
                                onValueChanged: (e: any) => {
                                    this.formData.Roles = this.formData.RoleList?.join(";");
                                    this.generateFormItems();
                                }
                            },
                        },

                        {
                            dataField: 'Enabled',
                            label: { text: 'Status' },
                            editorType: 'dxLookup',
                            editorOptions: {
                                dataSource: [
                                    { value: true, text: 'Active' },
                                    { value: false, text: 'Inactive' },
                                ],
                                valueExpr: 'value',
                                displayExpr: 'text',
                            },
                        },
                    ],
                },
                //
                // CONTEXTS
                //
                {
                    itemType: 'group',
                    colCount: 1,
                    caption: 'Contexts',
                    items: [
                        {
                            dataField: 'HWContext',
                            isRequired: true,
                            label: { text: 'H&W' },
                            editorType: 'dxLookup',
                            editorOptions: {
                                dataSource: ['EB', 'Affinity'],
                            },
                        },
                    ],
                },

                //
                // CLIENTS
                //
                {
                    itemType: 'group',
                    colCount: 1,
                    caption: 'Client(s)',
                    visible: formClientGroupVisible,
                    items: [
                        {
                            dataField: 'XpressClientGroupId',
                            isRequired: dataFieldClientGroupRequired,
                            editorType: 'dxLookup',
                            editorOptions: {
                                readOnly: this.$readOnly(),
                                searchEnabled: true,
                                showClearButton: true,
                                dataSource: dataSourceClientGroups,
                                valueExpr: 'Id',
                                displayExpr: 'Name',
                                onSelectionChanged: (e?: { selectedItem: any; }) => {
                                    this.selectedGroup = e?.selectedItem;
                                },
                                onValueChanged: () => {
                                    this.formData!.ClientIds = [];
                                    this.formData!.Clients = [];
                                    this.generateFormItems();
                                },
                            },
                        },


                        /*
                        {
                            dataField: 'ClientIds',
                            editorType: 'dxTagBox',
                            isRequired: this.formData!.XpressClientGroupId == 5657,
                            editorOptions: {
                                searchEnabled: true,
                                searchExpr: ['Name'],
                                showClearButton: true,
                                openOnFieldClick: true,
                                showSelectionControls: true,
                                showDropDownButton: true,
                                readOnly: this.$readOnly() || !hasValue(this.formData!.XpressClientGroupId) || this.formData!.XpressClientGroupId == 0,
                                dataSource:
                                    this.storeService.DataSourceOptions('Admin/Clients', 'Id',
                                        {
                                            sort: [{ selector: 'Name' },],
                                            select: ['Id', 'Name', 'ClientGroupId',],
                                            filter: ['ClientGroupId', '=', this.formData!.XpressClientGroupId,],
                                        }
                                    ),
                                placeholder:
                                    this.translateService.instant(!hasValue(this.formData!.XpressClientGroupId) ||
                                        this.formData!.XpressClientGroupId == 0
                                        ? 'ADMIN.ACCOUNTS.FIELD.ClientIds_Placeholder_NoGroup'
                                        : 'ADMIN.ACCOUNTS.FIELD.ClientIds_Placeholder'
                                    ),
                                valueExpr: 'Id',
                                displayExpr: 'Name',
                            },
                        },
                        */

                        {
                            dataField: 'Clients',
                            template: 'clientsTemplate',
                        },
                    ],
                },
            ] as DxFormItem[]
        ).map(mapFormAddTranslatedLabels(this.translateService, 'ADMIN.ACCOUNTS.FIELD'));

        setTimeout(() => {
            debugger
            this.$grid()?.updateDataSource(this.formData?.Clients?.map(x => ({
                ...x,
                UserId: x.UserId || this.formData!.Id,
                ClientGroupId: this.formData!.XpressClientGroupId,
                ClientId: x.ClientId || x['Id'],
                AllowHealth: x.AllowHealth ?? true,
                AllowAutoFleet: x.AllowAutoFleet ?? false,
            })));
        }, 321);

        this.loadIndicatorVisible = false;
    }

    tabs: Array<{ id: string, text: string, icon?: string }> = [
        { id: 'form', text: this.translateService.instant('COMMON.LABEL_DETAILS'), icon: 'fa-solid fa-circle-info' },
        { id: 'permissions', text: this.translateService.instant('PERMISSIONS.LABEL_EFFECTIVE_PERMISSIONS'), icon: 'fa-solid fa-user-lock' },
    ]

    $selectedTabIndex = signal<number>(0);
}
